import { Location, NavigateFunction, Params, RouteObject } from 'react-router-dom'

import { ApolloClient, Observable } from '@apollo/client'
import { ModuleId } from '@factorialco/f0-react/dist/experimental'
import { GetCurrentQuery } from 'generated/resources/graphql'

import { FixedDuration } from 'modules/core/components/DateNavigator/constants'
import { Cycle } from 'modules/core/components/DateNavigator/cycleUtils'
import { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'

import type { HandleData } from 'modules/layout/components/ResponsivePage/FactorialOnePage/useResolvedHandles'

import { Meta, ModuleMeta, ResourceMeta } from './types'

export interface Match {
  pathname: string
  handle?: RouteHandle | ModuleRouteHandle | ResourceRouteHandle
}
export interface DateNavigation {
  default: {
    cycle?: Cycle
    regularPeriods?: FixedDuration[]
    startDate: Date
    endDate: Date
  }
}

export type MetaContext = {
  watchQuery: ApolloClient<unknown>['watchQuery']
  apolloClient: ApolloClient<unknown>
  navigate: NavigateFunction
  navigation: Navigation<NavigationPaths>
  params: Params
  location: Location
  session: GetCurrentQuery['apiCore']['currentsConnection']['edges'][number]['node']
}

export type MetaValue<T> = Promise<T> | Observable<T> | T
export type ResolvableMeta<T> = (context: MetaContext) => MetaValue<T>

export type RouteHandle = {
  meta?: ResolvableMeta<Meta>
  resource?: false
  responsive?: boolean
  module?: never
  filters?: {
    date?: DateNavigation
  }
}

export type ModuleRouteHandle = {
  meta?: ResolvableMeta<ModuleMeta>
  module: ModuleId
  moduleName: (() => string) | null
}

export type ResourceRouteHandle = {
  meta?: ResolvableMeta<ResourceMeta>
  resource: true
}

export type RouteObjectWithMeta = Omit<RouteObject, 'handle' | 'children'> & {
  handle?: RouteHandle | ModuleRouteHandle | ResourceRouteHandle
  children?: RouteObjectWithMeta[]
}

export type ModuleRouteObject = Omit<RouteObject, 'handle' | 'children'> & {
  handle?: ModuleRouteHandle
  children?: RouteObjectWithMeta[]
}

export const getCurrentModule = (handles: HandleData[], moduleIndex: number) => {
  const handleData = handles.at(moduleIndex)

  if (handleData?.type !== 'module') return undefined
  const meta = !handleData.loading && !handleData.error ? handleData.meta : undefined

  return {
    name: (handleData.handle.moduleName ? handleData.handle.moduleName() : meta?.title) || '',
    path: handleData.path,
    ...meta,
    id: handleData.handle.module,
  }
}
